import { Slideshow } from './velune-audit-backup-slideshow.js';

/*
 * VELUNE — Slideshow viewport performance layer
 *
 * Keeps the theme's original Slideshow implementation intact in
 * velune-audit-backup-slideshow.js and prevents autoplay timers from
 * running while a slideshow is outside the viewport.
 *
 * The native slideshow already maintains the `in-viewport` attribute
 * through a shared IntersectionObserver. We reuse that signal instead
 * of creating another geometry observer.
 */

const nativePlay = Slideshow.prototype.play;
const nativeResume = Slideshow.prototype.resume;

/**
 * Synchronize autoplay with the native viewport state.
 * @param {Slideshow} slideshow
 */
function syncViewportAutoplay(slideshow) {
  if (!(slideshow instanceof Slideshow) || !slideshow.autoplay) return;

  if (slideshow.hasAttribute('in-viewport') && !document.hidden) {
    slideshow.resume();
  } else {
    slideshow.suspend();
  }
}

/**
 * Do not create an autoplay interval for an offscreen slideshow.
 */
Slideshow.prototype.play = function (interval = this.autoplayInterval) {
  if (!this.hasAttribute('in-viewport') || document.hidden) {
    this.suspend();
    return;
  }

  return nativePlay.call(this, interval);
};

/**
 * Any native resume path (hover leave, page visibility, etc.) must
 * still respect viewport visibility.
 */
Slideshow.prototype.resume = function () {
  if (!this.hasAttribute('in-viewport') || document.hidden) {
    this.suspend();
    return;
  }

  return nativeResume.call(this);
};

/*
 * The native shared IntersectionObserver toggles `in-viewport`.
 * Watching only this one attribute is substantially cheaper than
 * introducing another IntersectionObserver for the same geometry.
 */
const viewportAttributeObserver = new MutationObserver((records) => {
  for (const record of records) {
    const target = record.target;

    if (target instanceof Slideshow) {
      syncViewportAutoplay(target);
    }
  }
});

viewportAttributeObserver.observe(document.documentElement, {
  subtree: true,
  attributes: true,
  attributeFilter: ['in-viewport'],
});

/*
 * Existing slideshow elements can be upgraded synchronously while the
 * original module is imported, so normalize their timer state once.
 */
queueMicrotask(() => {
  document.querySelectorAll('slideshow-component').forEach((slideshow) => {
    if (slideshow instanceof Slideshow) {
      syncViewportAutoplay(slideshow);
    }
  });
});

export { Slideshow };
